// App recovery after deploys. A deploy replaces the hashed JS chunks; a tab still running the old build then
// fails to load a lazy page chunk on its next navigation, which used to unmount the app (black screen).
// Recovery: reload once so the tab picks up the new build. Guarded per tab so a genuinely broken build
// can never reload-loop (at most one automatic reload per RELOAD_GUARD_MS).

const RELOAD_KEY = 'chainsale.recovery.lastReloadAt';
export const RELOAD_GUARD_MS = 15_000;

// Lazy-chunk failures across browsers ("Failed to fetch dynamically imported module", "Importing a module
// script failed", "error loading dynamically imported module", Vite preload errors, webpack-style ChunkLoadError)
export const isChunkLoadError = (error: unknown): boolean => {
  const name = error instanceof Error ? error.name : '';
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return name === 'ChunkLoadError'
    || /dynamically imported module|Importing a module script failed|Unable to preload CSS|Failed to fetch dynamically/i.test(message);
};

// Pure: allow an automatic reload unless one happened within the guard window
export const shouldAutoReload = (lastReloadAt: number | null, now: number, guardMs: number = RELOAD_GUARD_MS): boolean =>
  lastReloadAt === null || now - lastReloadAt > guardMs;

const _readLast = (): number | null => {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY);
    return raw === null ? null : Number(raw);
  } catch { return null; }
};

// Reload once to load the new build; returns false when the guard blocked it (show the manual fallback)
export const reloadForNewBuild = (now: number = Date.now()): boolean => {
  if (!shouldAutoReload(_readLast(), now)) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(now)); } catch { /* storage blocked — still reload once */ }
  window.location.reload();
  return true;
};

// Vite fires 'vite:preloadError' when a dynamic import's chunk (or its CSS) fails to load
export const installChunkRecovery = (): void => {
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadForNewBuild()) event.preventDefault();
  });
};
